import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { Redo as RedoIcon, Undo as UndoIcon } from '@mui/icons-material';
import type { DrawnZone } from './ZonesTab';

interface Point {
  x: number;
  y: number;
}

/** In-memory undo/redo for polygon draft points (Add Zone, before Enter / name step). */
interface DraftPointHistory {
  snapshots: Point[][];
  cursor: number;
}

const INITIAL_DRAFT_HISTORY: DraftPointHistory = {
  snapshots: [[]],
  cursor: 0,
};

/** Mark zone draft toolbar (Undo / Redo / Cancel) — dark pills on video preview. */
const DRAFT_TOOLBAR_BG = '#444444';
const DRAFT_TOOLBAR_BG_HOVER = '#555555';
const DRAFT_TOOLBAR_FG = '#FFFFFF';
const DRAFT_TOOLBAR_FG_DISABLED = 'rgba(255, 255, 255, 0.38)';
const DRAFT_TOOLBAR_RADIUS = '10px';

/**
 * Internal canvas resolution (must match <canvas width/height>).
 * Coordinates are in this space; the canvas is CSS-stretched to the container — HTML overlays must use % of the same parent to align.
 */
const CANVAS_INTERNAL_WIDTH = 600;
const CANVAS_INTERNAL_HEIGHT = 400;

/** Legacy canvas size label; POST/PUT /zones `resolution` is set from the live video/image native size in ZoneConfigurationPageNew. */
export const ZONE_COORDINATE_RESOLUTION = `${CANVAS_INTERNAL_WIDTH}X${CANVAS_INTERNAL_HEIGHT}`;

/** Horizontal gap between the first vertex and the label/input right edge (canvas px). */
const NAME_LABEL_GAP_FROM_VERTEX = 4;
const NAME_LABEL_GAP_ABOVE_POINT = 6;
const NAME_LABEL_HEIGHT = 24;
const NAME_LABEL_PADDING = 8;
/** Corner radius for canvas label + name input (keep in sync). */
const NAME_LABEL_BORDER_RADIUS = 4;
const NAME_LABEL_INPUT_MAX_WIDTH = 220;

const ZONE_LABEL_FONT_SPEC = '12px Roboto, sans-serif';

function measureZoneLabelTextWidth(ctx: CanvasRenderingContext2D, name: string): number {
  ctx.font = ZONE_LABEL_FONT_SPEC;
  return ctx.measureText(name.trim() || '\u00A0').width;
}

/** Label box width in canvas pixels — must match `drawZone` (same formula as canvas rect). */
function computeZoneLabelBoxWidth(textWidth: number): number {
  const raw = textWidth + NAME_LABEL_PADDING * 2;
  return Math.min(
    NAME_LABEL_INPUT_MAX_WIDTH,
    Math.max(NAME_LABEL_PADDING * 2 + 4, raw),
  );
}

/** Overlay positioning: same width as the drawn label (no separate min width). */
function zoneNameLabelBoxWidthFromName(name: string): number {
  if (typeof document === 'undefined') {
    return NAME_LABEL_PADDING * 2 + 48;
  }
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');
  if (!ctx) return NAME_LABEL_PADDING * 2 + 48;
  const tw = measureZoneLabelTextWidth(ctx, name);
  return computeZoneLabelBoxWidth(tw);
}

/** Rounded rect path (fill + stroke) — canvas has no CSS; matches `NAME_LABEL_BORDER_RADIUS`. */
function fillStrokeRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
) {
  const r = Math.min(radius, w / 2, h / 2);
  ctx.beginPath();
  const rr = ctx as CanvasRenderingContext2D & {
    roundRect?: (x: number, y: number, w: number, h: number, r: number) => void;
  };
  if (typeof rr.roundRect === 'function') {
    rr.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
  ctx.fill();
  ctx.stroke();
}

/** Top-left of name label box (canvas coords): entire box is to the left of the start vertex. */
function nameLabelRectTopLeft(start: Point, boxWidth: number): Point {
  const rawX = start.x - NAME_LABEL_GAP_FROM_VERTEX - boxWidth;
  const x = Math.max(0, rawX);
  return {
    x,
    y: Math.max(0, start.y - NAME_LABEL_HEIGHT - NAME_LABEL_GAP_ABOVE_POINT),
  };
}

/** Ray-casting point-in-polygon check */
function pointInPolygon(px: number, py: number, vertices: Point[]): boolean {
  if (vertices.length < 3) return false;
  let inside = false;
  const n = vertices.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = vertices[i].x, yi = vertices[i].y;
    const xj = vertices[j].x, yj = vertices[j].y;
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Squared distance from P to segment AB — used to drag a line zone without vertex handles (tab edit).
 */
function squaredDistanceToSegment(
  px: number,
  py: number,
  a: Point,
  b: Point,
): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const apx = px - a.x;
  const apy = py - a.y;
  const abLen2 = abx * abx + aby * aby;
  if (abLen2 < 1e-10) {
    const dx = px - a.x;
    const dy = py - a.y;
    return dx * dx + dy * dy;
  }
  let t = (apx * abx + apy * aby) / abLen2;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * abx;
  const cy = a.y + t * aby;
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy;
}

/** Max distance (px) from pointer to line segment to start whole-line drag in locked edit mode. */
const LINE_ZONE_DRAG_HIT_PX = 14;

interface ZoneDrawingCanvasProps {
  isDrawing: boolean;
  /** Return `false` to reject save (e.g. duplicate name); canvas stays in name/edit state. */
  onZoneSaved: (zone: DrawnZone) => boolean | void | Promise<boolean | void>;
  onCancelDrawing: () => void;
  existingZones?: DrawnZone[];
  editingZone?: DrawnZone | null;
  /**
   * When the Mark Zones draft toolbar (Undo / Redo / Cancel) is visible — i.e. add-zone
   * polygon draft before completion — parent can lock navigation (back, tabs).
   */
  onDraftToolbarActiveChange?: (active: boolean) => void;
  /**
   * When set, Undo/Redo/Cancel and the instruction note render into this element (e.g. the
   * black preview shell) instead of inside the scaled zoom layer — so they stay visible when
   * Mark Zones zoom is not 100%.
   */
  draftControlsPortalEl?: HTMLElement | null;
  /** Zones tab row selection — draw this zone with an emphasized outline on Mark Zones. */
  highlightedZoneId?: string | null;
}

export function ZoneDrawingCanvas({
  isDrawing,
  onZoneSaved,
  onCancelDrawing,
  existingZones = [],
  editingZone = null,
  onDraftToolbarActiveChange,
  draftControlsPortalEl = null,
  highlightedZoneId = null,
}: ZoneDrawingCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onDraftToolbarActiveChangeRef = useRef(onDraftToolbarActiveChange);
  onDraftToolbarActiveChangeRef.current = onDraftToolbarActiveChange;
  const [points, setPoints] = useState<Point[]>([]);
  const [draftHistory, setDraftHistory] = useState<DraftPointHistory>(
    INITIAL_DRAFT_HISTORY,
  );
  const [isCompleted, setIsCompleted] = useState(false);
  const [zoneName, setZoneName] = useState('');
  const [nameInputPosition, setNameInputPosition] = useState<Point | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [draggedPoint, setDraggedPoint] = useState<number | null>(null);
  const [polygonDragStart, setPolygonDragStart] = useState<Point | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const inDraftPhase = isDrawing && !isCompleted && !editingZone;

  useEffect(() => {
    onDraftToolbarActiveChangeRef.current?.(inDraftPhase);
    return () => {
      if (inDraftPhase) {
        onDraftToolbarActiveChangeRef.current?.(false);
      }
    };
  }, [inDraftPhase]);

  const activePoints = useMemo(() => {
    if (inDraftPhase) {
      return draftHistory.snapshots[draftHistory.cursor] ?? [];
    }
    return points;
  }, [inDraftPhase, draftHistory, points]);

  const finishZoneSave = useCallback(
    async (newZone: DrawnZone) => {
      const result = onZoneSaved(newZone);
      const ok = result instanceof Promise ? await result : result;
      if (ok === false) return;
      setPoints([]);
      setDraftHistory(INITIAL_DRAFT_HISTORY);
      setIsCompleted(false);
      setZoneName('');
      setNameInputPosition(null);
      setIsEditMode(false);
    },
    [onZoneSaved],
  );

  const POINT_RADIUS = 6;
  const MIN_POINTS_POLYGON = 3;
  const MAX_POINTS = 6;
  const LINE_POINTS = 2;

  /** Convert mouse event position to canvas coordinate space (canvas is 600x400; display may be scaled) */
  const getCanvasCoords = (e: { clientX: number; clientY: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    return {
      x: Math.max(0, Math.min(canvas.width, x)),
      y: Math.max(0, Math.min(canvas.height, y)),
    };
  };

  // Reset state when drawing starts or editing starts
  useEffect(() => {
    if (isDrawing) {
      if (editingZone) {
        // Edit mode: load existing zone data; name input at first coordinate
        setPoints(editingZone.points);
        setZoneName(editingZone.name);
        setIsCompleted(true);
        setIsEditMode(true);
        const start = editingZone.points[0] ?? { x: 0, y: 0 };
        setNameInputPosition(
          nameLabelRectTopLeft(start, zoneNameLabelBoxWidthFromName(editingZone.name)),
        );
      } else {
        // New drawing mode
        setPoints([]);
        setDraftHistory(INITIAL_DRAFT_HISTORY);
        setIsCompleted(false);
        setZoneName('');
        setNameInputPosition(null);
        setIsEditMode(false);
      }
    }
  }, [isDrawing, editingZone]);

  /** Keep the name field aligned with the zone's starting coordinate while editing geometry. */
  useEffect(() => {
    if (!isDrawing || !isCompleted || points.length === 0) return;
    const next = nameLabelRectTopLeft(
      points[0],
      zoneNameLabelBoxWidthFromName(zoneName),
    );
    setNameInputPosition((prev) => {
      if (prev && prev.x === next.x && prev.y === next.y) return prev;
      return next;
    });
  }, [isDrawing, isCompleted, points, zoneName]);

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw existing zones — highlighted zone last so it paints on top (Zones tab row click).
    const zonesSorted = [...existingZones].sort((a, b) => {
      const ah = highlightedZoneId != null && a.id === highlightedZoneId ? 1 : 0;
      const bh = highlightedZoneId != null && b.id === highlightedZoneId ? 1 : 0;
      return ah - bh;
    });
    zonesSorted.forEach((zone) => {
      const emphasize =
        highlightedZoneId != null && zone.id === highlightedZoneId;
      drawZone(ctx, zone.points, zone.color, zone.type, true, zone.name, {
        drawLabel: true,
        vertexHoverIndex: null,
        emphasize,
      });
    });

    // Draw current zone being drawn
    if (activePoints.length > 0) {
      const zoneType =
        activePoints.length === LINE_POINTS ? 'line' : 'polygon';
      const hideCanvasNameWhileTyping =
        isCompleted && nameInputPosition != null;
      drawZone(ctx, activePoints, '#FF8555', zoneType, isCompleted, zoneName, {
        drawLabel: !hideCanvasNameWhileTyping,
        // Zones tab Edit: move whole shape only — no vertex hover affordance.
        vertexHoverIndex: isEditMode ? null : hoveredPoint,
        emphasize: Boolean(editingZone),
      });
    }
  }, [
    activePoints,
    isCompleted,
    isEditMode,
    zoneName,
    existingZones,
    nameInputPosition,
    hoveredPoint,
    highlightedZoneId,
  ]);

  const drawZone = (
    ctx: CanvasRenderingContext2D,
    zonePoints: Point[],
    color: string,
    type: 'polygon' | 'line',
    completed: boolean,
    name?: string,
    options?: {
      drawLabel?: boolean;
      vertexHoverIndex?: number | null;
      emphasize?: boolean;
    },
  ) => {
    const drawLabel = options?.drawLabel !== false;
    const vertexHoverIndex = options?.vertexHoverIndex ?? null;
    const emphasize = options?.emphasize === true;
    if (zonePoints.length === 0) return;

    ctx.save();

    // Draw filled polygon or line
    if (completed && type === 'polygon' && zonePoints.length >= MIN_POINTS_POLYGON) {
      ctx.beginPath();
      ctx.moveTo(zonePoints[0].x, zonePoints[0].y);
      for (let i = 1; i < zonePoints.length; i++) {
        ctx.lineTo(zonePoints[i].x, zonePoints[i].y);
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(179, 144, 115, 0.4)'; // Brown fill with transparency
      ctx.fill();
    }

    // Draw lines (optional white halo when emphasized — matches Zones tab selection)
    const traceOutlinePath = () => {
      ctx.beginPath();
      ctx.moveTo(zonePoints[0].x, zonePoints[0].y);
      for (let i = 1; i < zonePoints.length; i++) {
        ctx.lineTo(zonePoints[i].x, zonePoints[i].y);
      }
      if (completed && type === 'polygon' && zonePoints.length >= MIN_POINTS_POLYGON) {
        ctx.closePath();
      }
    };
    if (emphasize) {
      traceOutlinePath();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
      ctx.lineWidth = 6;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = emphasize ? 3 : 2;
    traceOutlinePath();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();

    // Draw points (hover highlight only for the active editable zone)
    zonePoints.forEach((point, index) => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, POINT_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = vertexHoverIndex === index ? '#FFFFFF' : color;
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // Draw zone label at first coordinate when not using the HTML name input
    if (completed && name && drawLabel && zonePoints.length > 0) {
      const start = zonePoints[0];
      const textWidth = measureZoneLabelTextWidth(ctx, name);
      const rectWidth = computeZoneLabelBoxWidth(textWidth);
      const topLeft = nameLabelRectTopLeft(start, rectWidth);
      const rectX = topLeft.x;
      const rectY = topLeft.y;
      const rectHeight = NAME_LABEL_HEIGHT;

      ctx.fillStyle = 'white';
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      fillStrokeRoundRect(
        ctx,
        rectX,
        rectY,
        rectWidth,
        rectHeight,
        NAME_LABEL_BORDER_RADIUS,
      );

      ctx.fillStyle = '#212121';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        name,
        rectX + NAME_LABEL_PADDING,
        rectY + rectHeight / 2,
      );
    }

    ctx.restore();
  };

  // Handle canvas click
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || isCompleted) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasCoords(e);

    setDraftHistory((h) => {
      const cur = h.snapshots[h.cursor] ?? [];
      if (cur.length >= MAX_POINTS) {
        return h;
      }
      const nextPts = [...cur, { x, y }];
      const base = h.snapshots.slice(0, h.cursor + 1);
      const snapshots = [...base, nextPts];
      return { snapshots, cursor: snapshots.length - 1 };
    });
  };

  // Handle mouse move for hover effect and drag
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasCoords(e);

    // Handle whole-polygon drag
    if (polygonDragStart !== null) {
      const dx = x - polygonDragStart.x;
      const dy = y - polygonDragStart.y;
      setPoints((prev) => prev.map((p) => ({ x: p.x + dx, y: p.y + dy })));
      setPolygonDragStart({ x, y });
      return;
    }

    // Vertex hover (skip in tab Edit mode — shape is move-only, name editable)
    const hoverSource = !isCompleted ? activePoints : points;
    const hitRadius = POINT_RADIUS + 4;
    let foundHover = false;
    if (!(isCompleted && isEditMode)) {
      for (let i = 0; i < hoverSource.length; i++) {
        const point = hoverSource[i];
        const distance = Math.sqrt((x - point.x) ** 2 + (y - point.y) ** 2);
        if (distance <= hitRadius) {
          setHoveredPoint(i);
          foundHover = true;
          break;
        }
      }
    }
    if (!foundHover) {
      setHoveredPoint(null);
    }

    // Handle vertex dragging (disabled when editing an existing zone from the Zones tab)
    if (draggedPoint !== null && !isEditMode) {
      setPoints((prev) => {
        const newPoints = [...prev];
        newPoints[draggedPoint] = { x, y };
        return newPoints;
      });
    }
  };

  // Handle mouse down for dragging (vertex or whole polygon)
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isCompleted) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasCoords(e);

    // Vertex reshape — only while drawing a new zone (not Zones-tab Edit on existing zone)
    if (!isEditMode) {
      const hitRadius = POINT_RADIUS + 4;
      for (let i = 0; i < points.length; i++) {
        const point = points[i];
        const distance = Math.sqrt((x - point.x) ** 2 + (y - point.y) ** 2);
        if (distance <= hitRadius) {
          setDraggedPoint(i);
          return;
        }
      }
    }

    // Whole-shape drag: polygon interior, or near line segment when vertices are locked
    if (points.length >= MIN_POINTS_POLYGON && pointInPolygon(x, y, points)) {
      setPolygonDragStart({ x, y });
    } else if (
      isEditMode &&
      points.length === LINE_POINTS
    ) {
      const maxD = LINE_ZONE_DRAG_HIT_PX;
      if (
        squaredDistanceToSegment(x, y, points[0], points[1]) <=
        maxD * maxD
      ) {
        setPolygonDragStart({ x, y });
      }
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setDraggedPoint(null);
    setPolygonDragStart(null);
  };

  // Handle key press
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isDrawing) return;

      if (e.key === 'Enter') {
        const draft =
          draftHistory.snapshots[draftHistory.cursor] ?? [];
        if (!isCompleted && draft.length >= LINE_POINTS) {
          // Complete the zone and show name input to the left of the start vertex
          setPoints(draft.map((p) => ({ ...p })));
          const start = draft[0] ?? { x: 0, y: 0 };
          const defaultName = `Zone ${(existingZones.length + 1).toString().padStart(2, '0')}`;
          setZoneName(defaultName);
          setIsCompleted(true);
          setNameInputPosition(
            nameLabelRectTopLeft(start, zoneNameLabelBoxWidthFromName(defaultName)),
          );
        } else if (isCompleted && zoneName.trim()) {
          // Name field handles Enter with stopPropagation; avoid double POST if event still reaches window
          const t = e.target;
          if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) {
            return;
          }
          // Save the zone
          const zoneType = points.length === LINE_POINTS ? 'line' : 'polygon';
          const newZone: DrawnZone = {
            id: isEditMode && editingZone ? editingZone.id : `zone-${Date.now()}`,
            name: zoneName.trim(),
            type: zoneType,
            points: points,
            color: '#FF8555',
            isActive: true,
            createdAt: isEditMode && editingZone ? editingZone.createdAt : new Date(),
          };
          void finishZoneSave(newZone);
        }
      } else if (e.key === 'Escape') {
        // Cancel drawing
        onCancelDrawing();
        setPoints([]);
        setDraftHistory(INITIAL_DRAFT_HISTORY);
        setIsCompleted(false);
        setZoneName('');
        setNameInputPosition(null);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [
    isDrawing,
    isCompleted,
    points,
    draftHistory,
    zoneName,
    existingZones,
    finishZoneSave,
    onCancelDrawing,
    isEditMode,
    editingZone,
  ]);

  const handleDraftUndo = () => {
    setDraftHistory((h) =>
      h.cursor > 0 ? { ...h, cursor: h.cursor - 1 } : h,
    );
  };

  const handleDraftRedo = () => {
    setDraftHistory((h) =>
      h.cursor < h.snapshots.length - 1
        ? { ...h, cursor: h.cursor + 1 }
        : h,
    );
  };

  const handleDraftToolbarCancel = () => {
    setDraftHistory(INITIAL_DRAFT_HISTORY);
    setPoints([]);
    setIsCompleted(false);
    setZoneName('');
    setNameInputPosition(null);
    onCancelDrawing();
  };

  const canDraftUndo = draftHistory.cursor > 0;
  const canDraftRedo = draftHistory.cursor < draftHistory.snapshots.length - 1;

  // Get instruction message
  const getInstructionMessage = () => {
    if (!isDrawing) return null;
    
    if (!isCompleted) {
      const n = activePoints.length;
      if (n === 0) {
        return 'Start drawing zone';
      } else if (n === 1) {
        return 'Note: 3 points are required to draw a polygon.\nPress Enter to save zone';
      } else if (n === LINE_POINTS) {
        return 'Note: 3 points are required to draw a polygon.\nPress Enter to save zone';
      } else if (n < MIN_POINTS_POLYGON) {
        return 'Note: 3 points are required to draw a polygon.\nPress Enter to save zone';
      } else {
        return 'Note: 3 points are required to draw a polygon.\nPress Enter to save zone';
      }
    } else {
      return 'Press Enter to save zone';
    }
  };

  const instructionMessage = getInstructionMessage();

  const markZonesDraftChrome = (
    <>
      {/* Undo / Redo / Cancel — Add Zone draft only (bottom-left); charcoal #444 + white icons/text. */}
      {inDraftPhase && (
        <Box
          role="toolbar"
          aria-label="Zone drawing actions"
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            zIndex: 11,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            pointerEvents: draftControlsPortalEl ? 'auto' : undefined,
          }}
        >
          <IconButton
            aria-label="Undo"
            disabled={!canDraftUndo}
            onClick={(e) => {
              e.stopPropagation();
              handleDraftUndo();
            }}
            sx={{
              width: 40,
              height: 36,
              borderRadius: DRAFT_TOOLBAR_RADIUS,
              bgcolor: DRAFT_TOOLBAR_BG,
              color: DRAFT_TOOLBAR_FG,
              '&:hover': {
                bgcolor: DRAFT_TOOLBAR_BG_HOVER,
              },
              '&.Mui-disabled': {
                bgcolor: DRAFT_TOOLBAR_BG,
                color: DRAFT_TOOLBAR_FG_DISABLED,
              },
            }}
          >
            <UndoIcon sx={{ fontSize: 22, color: '#ffffff' }} />
          </IconButton>
          <IconButton
            aria-label="Redo"
            disabled={!canDraftRedo}
            onClick={(e) => {
              e.stopPropagation();
              handleDraftRedo();
            }}
            sx={{
              width: 40,
              height: 36,
              borderRadius: DRAFT_TOOLBAR_RADIUS,
              bgcolor: DRAFT_TOOLBAR_BG,
              color: DRAFT_TOOLBAR_FG,
              '&:hover': {
                bgcolor: DRAFT_TOOLBAR_BG_HOVER,
              },
              '&.Mui-disabled': {
                bgcolor: DRAFT_TOOLBAR_BG,
                color: DRAFT_TOOLBAR_FG_DISABLED,
              },
            }}
          >
            <RedoIcon sx={{ fontSize: 22, color: '#ffffff' }} />
          </IconButton>
          <Button
            variant="contained"
            disableElevation
            onClick={(e) => {
              e.stopPropagation();
              handleDraftToolbarCancel();
            }}
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              fontWeight: 500,
              minWidth: 88,
              height: 36,
              px: 2,
              py: 0.5,
              borderRadius: DRAFT_TOOLBAR_RADIUS,
              textTransform: 'none',
              bgcolor: DRAFT_TOOLBAR_BG,
              color: DRAFT_TOOLBAR_FG,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: DRAFT_TOOLBAR_BG_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      )}

      {instructionMessage && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            bgcolor: 'rgba(255, 255, 255, 0.3)',
            color: 'white',
            px: 2,
            py: 1.5,
            borderRadius: '8px',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '13px',
            lineHeight: '18px',
            pointerEvents: 'none',
            zIndex: 10,
            whiteSpace: 'pre-line',
            textAlign: 'left',
          }}
        >
          {instructionMessage}
        </Box>
      )}
    </>
  );

  // Always render the canvas to show existing zones
  return (
    <>
      <canvas
        ref={canvasRef}
        width={CANVAS_INTERNAL_WIDTH}
        height={CANVAS_INTERNAL_HEIGHT}
        onClick={handleCanvasClick}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          cursor: isDrawing ? (isCompleted ? (draggedPoint !== null || polygonDragStart !== null ? 'grabbing' : 'grab') : 'crosshair') : 'default',
          pointerEvents: isDrawing ? 'auto' : 'none',
        }}
      />

      {draftControlsPortalEl
        ? (createPortal(
            markZonesDraftChrome,
            draftControlsPortalEl,
          ) as unknown as React.ReactNode)
        : markZonesDraftChrome}

      {/* Zone Name Input */}
      {isCompleted && nameInputPosition && (
        <Box
          sx={{
            position: 'absolute',
            left: `${(nameInputPosition.x / CANVAS_INTERNAL_WIDTH) * 100}%`,
            top: `${(nameInputPosition.y / CANVAS_INTERNAL_HEIGHT) * 100}%`,
            width: `${(zoneNameLabelBoxWidthFromName(zoneName) / CANVAS_INTERNAL_WIDTH) * 100}%`,
            zIndex: 20,
            boxSizing: 'border-box',
          }}
        >
          <TextField
            autoFocus
            fullWidth
            margin="none"
            size="small"
            value={zoneName}
            onChange={(e) => setZoneName(e.target.value)}
            placeholder="Enter zone name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                if (zoneName.trim()) {
                  const zoneType = points.length === LINE_POINTS ? 'line' : 'polygon';
                  const newZone: DrawnZone = {
                    id: isEditMode && editingZone ? editingZone.id : `zone-${Date.now()}`,
                    name: zoneName.trim(),
                    type: zoneType,
                    points: points,
                    color: '#FF8555',
                    isActive: true,
                    createdAt: isEditMode && editingZone ? editingZone.createdAt : new Date(),
                  };
                  void finishZoneSave(newZone);
                }
              } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onCancelDrawing();
                setPoints([]);
                setDraftHistory(INITIAL_DRAFT_HISTORY);
                setIsCompleted(false);
                setZoneName('');
                setNameInputPosition(null);
              }
            }}
            sx={{
              m: 0,
              width: '100%',
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                height: NAME_LABEL_HEIGHT,
                minHeight : 36,
                padding: '0 8px',
                boxSizing: 'border-box',
                borderRadius: `${NAME_LABEL_BORDER_RADIUS}px`,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FF8555',
              },
              '& .MuiOutlinedInput-input': {
                padding: '0 0 0 0',
                height: NAME_LABEL_HEIGHT,
                lineHeight: `${NAME_LABEL_HEIGHT}px`,
                boxSizing: 'border-box',
              },
            }}
          />
        </Box>
      )}
    </>
  );
}